/**
 * mentionMixin — shared Alpine.js mixin for @mention search/select UX.
 *
 * Provides state and methods for detecting "@" triggers, searching users,
 * navigating results with the keyboard, and selecting a result. Consumers
 * must override `insertMentionResult(result)` to define how the selected
 * user is inserted into the input (e.g. plain @name text or a token).
 *
 * Token helpers (addMentionedUser, tokenizeContent, resetMentionedUsers):
 *   Used by consumers that store @{{uuid}} tokens in the DB but display
 *   @Name in the textarea. Call addMentionedUser when a result is selected,
 *   tokenizeContent before submitting, and resetMentionedUsers after send.
 *
 * Usage:
 *   import { mentionMixin } from '../shared/mention-mixin';
 *
 *   // In an Alpine component factory:
 *   return {
 *     ...mentionMixin(),
 *     insertMentionResult(result) { ... },  // override
 *   };
 *
 *   // In the textarea:
 *   @input="handleMentionInput($event)"
 *
 *   // In the keydown handler, delegate first:
 *   if (this.handleMentionKeydown(event)) return;
 */

import i18n from 'app/i18n';

export function mentionMixin() {
  return {
    // ── State ────────────────────────────────────────────────────────────────
    announceRef: '',
    mentionStartIndex: -1,
    mentionString: '',
    // Map of uniqueName → { uuid, name, id } for tokenizing @Name → @{{uuid}} on submit.
    mentionedUsers: {},
    results: [],
    resultsQueryLimit: 15,
    searching: false,
    selectedResultIndex: -1,
    showSearchPrompt: false,
    showSearchResults: false,

    // ── Announcements ────────────────────────────────────────────────────────
    setAnnouncement(message) {
      this.announceRef = message;
      setTimeout(() => this.clearAnnouncement(), 1000);
    },

    clearAnnouncement() {
      this.announceRef = '';
    },

    // ── Trigger detection ────────────────────────────────────────────────────
    isLastCharMentionToken(value, index) {
      return value.slice(index - 1, index) === '@';
    },

    isCharEmptyOrNewline(character) {
      if (typeof character === 'undefined') return false;
      const match = character.match(/[ |\r\n]/gi);
      return !!(match && match.length > 0);
    },

    isPrevLastCharValid(value, index) {
      let prevLastChar = null;
      if (value.length > 2 && index >= 2) {
        prevLastChar = value.slice(index - 2, index - 1);
      }
      if (prevLastChar === null) return true;
      return this.isCharEmptyOrNewline(prevLastChar);
    },

    shouldTriggerMention(value, index) {
      return (
        this.isLastCharMentionToken(value, index) &&
        this.isPrevLastCharValid(value, index)
      );
    },

    // ── User search ──────────────────────────────────────────────────────────
    searchUsers() {
      const mentionString = this.mentionString;
      this.searching = true;

      if (mentionString !== this.mentionString) return;

      this.$api({ url: '/users/search', query: { name: this.mentionString, per_page: this.resultsQueryLimit } })
        .then((data) => {
          this.results = data.data.data;
          this.searching = false;
          this.selectedResultIndex = -1;
          return data;
        })
        .then((data) => {
          const numResults = data.data.data.length;
          if (numResults === 0) {
            return this.setAnnouncement(
              i18n.t('posts.No_scholars_found_for_searchterm', { searchTerm: this.mentionString.substr(1) })
            );
          }
          const scholars = numResults > 1 ? i18n.t('posts.scholars') : i18n.t('posts.scholar');
          this.setAnnouncement(i18n.t('posts.n_scholars_found_announcement', { n: numResults, scholars }));
        })
        .then(() => {
          if (mentionString === this.mentionString) {
            this.searching = false;
          }
        });
    },

    // ── Input parsing ─────────────────────────────────────────────────────────
    onChangeParseMentions(value, index) {
      const mentionString = this.mentionStartIndex > -1 ? value.slice(this.mentionStartIndex, index) : '';
      let mentionStartIndex = mentionString.length <= 0 ? -1 : this.mentionStartIndex;
      const emptyMentionString = mentionString.trim().length < 3;
      const shouldTriggerMention = this.shouldTriggerMention(value, index);

      if (shouldTriggerMention) {
        mentionStartIndex = index - 1;
      }

      const announceSearchPrompt = shouldTriggerMention && !this.showSearchPrompt;
      const performSearch = !emptyMentionString && mentionStartIndex > -1;

      this.mentionStartIndex = mentionStartIndex;
      this.mentionString = mentionString;
      this.results = emptyMentionString ? [] : this.results;
      this.showSearchPrompt = shouldTriggerMention;
      this.showSearchResults = !emptyMentionString;

      if (announceSearchPrompt) {
        this.setAnnouncement(i18n.t('posts.Tag_started_announcement'));
      }

      if (performSearch) {
        const ua = navigator.userAgent.toLowerCase();
        const delay = ua.indexOf('safari') !== -1 && ua.indexOf('chrome') === -1 ? 750 : 350;
        setTimeout(() => {
          if (this.mentionString === mentionString) {
            this.searchUsers();
          }
        }, delay);
      }
    },

    /**
     * Call from the input's @input handler. Reads value and cursor from the event.
     */
    handleMentionInput(event) {
      this.onChangeParseMentions(event.target.value, event.target.selectionStart);
    },

    // ── Result navigation & selection ────────────────────────────────────────
    updateSelectedResultIndex(delta) {
      const len = this.results.length;
      const next = this.selectedResultIndex + delta;
      if (next >= len) {
        this.selectedResultIndex = 0;
      } else if (next < 0) {
        this.selectedResultIndex = len - 1;
      } else {
        this.selectedResultIndex = next;
      }
    },

    dismissMentionSearchResults() {
      this.searching = false;
      this.showSearchPrompt = false;
      this.showSearchResults = false;
      this.selectedResultIndex = -1;
      this.mentionStartIndex = -1;
      this.mentionString = '';
    },

    cancelMention() {
      if (this.mentionStartIndex < 0) return;
      this.dismissMentionSearchResults();
      this.mentionStartIndex = -1;
      this.mentionString = '';
      this.setAnnouncement(i18n.t('posts.Tag_cancelled_announcement'));
    },

    // ── Token helpers ─────────────────────────────────────────────────────────
    // Hidden soft-hyphen used to make duplicate display names unique without
    // affecting visual rendering in the textarea.
    _mentionHiddenChar: '\xAD',

    _isNameUnique(name) {
      return !Object.prototype.hasOwnProperty.call(this.mentionedUsers, name);
    },

    _generateUniqueName(name) {
      let unique = name;
      while (!this._isNameUnique(unique)) {
        unique += this._mentionHiddenChar;
      }
      return unique;
    },

    /**
     * Register a selected user in mentionedUsers and return the unique display
     * name to insert into the textarea. Call this from insertMentionResult.
     */
    addMentionedUser(result) {
      const { uuid, name } = result.attributes;
      // Re-use existing entry if this exact uuid was already added.
      const existing = Object.entries(this.mentionedUsers).find(([, v]) => v.uuid === uuid);
      if (existing) return existing[0];

      const uniqueName = this._generateUniqueName(name);
      this.mentionedUsers[uniqueName] = { uuid, name, id: result.id };
      return uniqueName;
    },

    /**
     * Replace every @UniqueName in text with @{{uuid}} for DB storage.
     * Call this on the content value just before submitting.
     */
    tokenizeContent(text) {
      let result = text;
      Object.entries(this.mentionedUsers).forEach(([uniqueName, { uuid }]) => {
        // Escape soft hyphens and other special regex chars in the unique name.
        const escaped = uniqueName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        result = result.replace(new RegExp(`@${escaped}`, 'g'), `@{{${uuid}}}`);
      });
      return result;
    },

    /**
     * Clear the mentionedUsers map. Call this after a message is sent.
     */
    resetMentionedUsers() {
      this.mentionedUsers = {};
    },

    /**
     * Hook — override in the consuming component to define how a selected user
     * is inserted into the input value.
     *
     * @param {Object} result - A user search result object from /users/search
     */
    insertMentionResult(_result) {
      // no-op — consumers must override this
    },

    onClickMentionResult(result) {
      if (!result) return;
      this.insertMentionResult(result);
      this.dismissMentionSearchResults();
    },

    /**
     * Delegate to this from the consumer's keydown handler. Returns true if the
     * event was consumed (so the caller can bail out early).
     *
     * @param {KeyboardEvent} event
     * @returns {boolean} whether the event was handled
     */
    handleMentionKeydown(event) {
      switch (event.key) {
        case 'Enter':
          if (!this.showSearchResults || this.searching) return false;
          event.preventDefault();
          event.stopPropagation();
          this.onClickMentionResult(this.results[this.selectedResultIndex]);
          return true;

        case 'Escape':
          if (this.mentionStartIndex >= 0) {
            event.preventDefault();
            event.stopPropagation();
            this.cancelMention();
            return true;
          }
          return false;

        case 'ArrowUp':
          if (!this.showSearchResults) return false;
          event.preventDefault();
          event.stopPropagation();
          this.updateSelectedResultIndex(-1);
          return true;

        case 'ArrowDown':
          if (!this.showSearchResults) return false;
          event.preventDefault();
          event.stopPropagation();
          this.updateSelectedResultIndex(1);
          return true;

        default:
          return false;
      }
    },
  };
}
